import { BasicContact } from '@bb/domain/dist/types';
import { Divider, Text, theme } from 'components';
import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from 'contexts';
import {
  formatPhoneNumber,
  formatPhoneNumberAsLink,
  isAuthForRole,
} from 'utils';
import {
  Container,
  ContentContainer,
  HeaderContainer,
  Section,
  StyledContent,
  TouchableValue,
  Value,
} from './common';
import Row from './Row';
import { findPublicBBUserInfoByMlsNumber } from '../../../../api/UserController';

interface CoopDrawerDetailsProps {
  contact: BasicContact;
}

const CoopDrawerDetails: React.FC<CoopDrawerDetailsProps> = ({ contact }) => {
  const { uid, boardId, email, phone, text, org } = contact;
  const { auth } = useContext(AuthContext);

  const [releaseCell, setReleaseCell] = useState(false);
  const [releaseWorkPhone, setReleaseWorkPhone] = useState(false);
  const [isAuthorizedToViewCell, setIsAuthorizedToViewCell] = useState(false);

  useEffect(() => {
    async function fetchBBUser() {
      if (uid && boardId) {
        const bbUser = await findPublicBBUserInfoByMlsNumber(uid, boardId);

        if (bbUser) {
          const isAuthorized =
            isAuthForRole({
              auth,
              org: bbUser.org,
              location: bbUser.location,
              teams: [bbUser.team],
            }) || auth?.id === bbUser.id;
          setIsAuthorizedToViewCell(isAuthorized);
          const releaseCellNumber =
            bbUser?.frontEndInstructions?.releaseCell || false;
          setReleaseCell(releaseCellNumber);
          setReleaseWorkPhone(releaseCellNumber);
        }
      } else {
        setReleaseWorkPhone(true);
      }
    }
    fetchBBUser();
  }, [auth, uid, boardId]);

  return (
    <Container>
      <HeaderContainer>
        <Text strong>Details</Text>
      </HeaderContainer>
      <Divider />

      <StyledContent>
        <ContentContainer>
          <Section data-cy="contacts-details-contactMethod">
            <Row label="Email">
              <TouchableValue href={`mailto:${email}`}>{email}</TouchableValue>
            </Row>
            {phone && releaseWorkPhone && (
              <Row label="Work">
                <TouchableValue href={formatPhoneNumberAsLink(phone)}>
                  {formatPhoneNumber(phone)}
                </TouchableValue>
              </Row>
            )}
            {/* If the user has opted to release their cell number, show it. If the
            user has not opted to release their cell number, show it only if the
            user is authorized to view it. */}
            {text &&
              (releaseCell ? (
                <Row label="Mobile">
                  <TouchableValue href={formatPhoneNumberAsLink(text)}>
                    {formatPhoneNumber(text)}
                  </TouchableValue>
                </Row>
              ) : (
                isAuthorizedToViewCell && (
                  <Row label="Mobile">
                    <TouchableValue href={formatPhoneNumberAsLink(text)}>
                      {formatPhoneNumber(text)}
                    </TouchableValue>
                    <Value style={{ color: theme.gray7 }}>
                      (Do Not Release)
                    </Value>
                  </Row>
                )
              ))}
            <Row label="Office">
              <TouchableValue
                href={org?.phone && formatPhoneNumberAsLink(org.phone)}
              >
                {org?.phone && formatPhoneNumber(org.phone)}
              </TouchableValue>
            </Row>
          </Section>
        </ContentContainer>
      </StyledContent>
    </Container>
  );
};

export default CoopDrawerDetails;
